import React from 'react';
import { useAlert } from '@weave/alert-system';
import {
  Modal,
  ModalControlModalProps,
  Text,
  ButtonBar,
  PrimaryButton,
  SecondaryButton,
} from '@weave/design-system';
import { API } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records-ext/call-quality/v1/api.pb';
import { authorizedFetch } from 'helpers/utils';
import { formatDate } from 'helpers/date';

export type ModalData = {
  ChannelID: string;
  direction: string;
  patientNumber: string;
  officeNumber: string;
  startedAt: string;
};

interface Props extends ModalControlModalProps {
  closeModal: () => void;
  canPerformActions: boolean;
  downloadCDR: (callId: string) => void;
  downloadCDRALeg: (callId: string) => void;
  modalData: ModalData;
}

export const CallRecordModal = ({
  closeModal,
  modalData,
  canPerformActions,
  downloadCDR,
  downloadCDRALeg,
  ...modalProps
}: Props) => {
  const alerts = useAlert();

  const handleTrace = async (fromNum: string, startedAt: Date) => {
    const startTime = formatDate(startedAt);

    try {
      const data = await API.Trace(authorizedFetch(), {
        fromNumber: {
          // hard code NANPA for now, update this when call records support i18n.
          countryCode: 1,
          nationalNumber: sanitizeNumber(fromNum),
        },
        startTime: startTime,
      });

      // open export link in new tab
      window.open(data.traceUrl, '_blank');
    } catch (error) {
      alerts.error('Failed to find hepic trace for ' + fromNum);
    }
  };

  const hepicSearch = () => {
    // if direction is inbound, fromNumber is the patientNumber
    // if direction is outbound, fromNumber is the officeNumber
    const fromNum =
      modalData.direction.toLowerCase() === 'inbound'
        ? modalData.patientNumber
        : modalData.officeNumber;

    const startedAt = callRecordDate(modalData.startedAt);
    handleTrace(fromNum, startedAt);
  };

  return (
    <Modal {...modalProps} maxWidth={800}>
      <Modal.Header>Call Record</Modal.Header>
      <Modal.Body>
        <br />
        <ul>
          <li>
            <Text>
              <strong>Channel ID</strong> {modalData.ChannelID}
            </Text>
          </li>
          <li>
            <Text>
              <strong>Patient Number:</strong> {modalData.patientNumber}
            </Text>
          </li>
          <li>
            <Text>
              <strong>Office Number:</strong> {modalData.officeNumber}
            </Text>
          </li>
          <li>
            <Text>
              <strong>Started At:</strong> {modalData.startedAt}
            </Text>
          </li>
          <li>
            <Text>
              <strong>Direction:</strong> {modalData.direction}
            </Text>
          </li>
        </ul>
        <ButtonBar>
          <PrimaryButton disabled={!canPerformActions} onClick={hepicSearch}>
            Hepic Search (3 day limit)
          </PrimaryButton>
          <PrimaryButton
            disabled={!canPerformActions}
            onClick={(e) => {
              downloadCDRALeg(modalData.ChannelID);
              e.stopPropagation();
            }}
          >
            Download A Leg CDR
          </PrimaryButton>
          <SecondaryButton
            disabled={!canPerformActions}
            onClick={(e) => {
              downloadCDR(modalData.ChannelID);
              e.stopPropagation();
            }}
          >
            Download Full CDR
          </SecondaryButton>
        </ButtonBar>
      </Modal.Body>

      <Modal.Actions onPrimaryClick={closeModal} primaryLabel="Close" />
    </Modal>
  );
};

// convert number string to national number digits for weavelab.xyz/phonenumbers
const sanitizeNumber = (s: string): number => {
  // Remove all non-digit characters from the number
  const sanitized = s.replace(/\D/g, '');

  return Number(sanitized);
};

// convert "05/05/2025 2:36 PM" string to a Date timestamp
const callRecordDate = (s: string): Date => {
  const date = new Date(s);
  return date;
};
